import { FC } from 'react'
import { Badge, Box, Button, Flex, Grid, Icon, Link, Text, VStack } from '@chakra-ui/react'
import { FiBarChart2, FiClock, FiTrendingUp, FiUsers } from 'react-icons/fi'
import { IconType } from 'react-icons'
import { IconBox } from '../../../common/components/IconBox'
import { DownloadIcon } from './ExportIcons'

interface AnalyticsComingSoonPanelProps {
  onGoToExports: () => void
}

const plannedFeatures: Array<{
  title: string
  description: string
  icon: IconType
}> = [
  {
    title: 'Prescription trends',
    description: 'Daily and monthly volume over time, with comparison to prior periods.',
    icon: FiTrendingUp,
  },
  {
    title: 'Top medications',
    description: 'Ranked breakdown of most-prescribed drugs with antimicrobial filtering.',
    icon: FiBarChart2,
  },
  {
    title: 'Prescriber breakdown',
    description: 'Volume comparison across team members and locations.',
    icon: FiUsers,
  },
]

export const AnalyticsComingSoonPanel: FC<AnalyticsComingSoonPanelProps> = ({ onGoToExports }) => {
  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="16px"
      px={{ base: 5, md: 8 }}
      py={{ base: 9, md: 12 }}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.06), transparent 60%)',
        pointerEvents: 'none',
      }}
    >
      <VStack spacing={0} position="relative" zIndex={1}>
        <Box mb="22px" position="relative">
          <IconBox
            boxSize={120}
            iconSize={56}
            borderRadius="24px"
            bgGradient="linear(to-br, blue.50, blue.100)"
          >
            <Icon as={FiBarChart2} boxSize="56px" color="blue.600" aria-hidden />
          </IconBox>
        </Box>

        <Badge
          display="inline-flex"
          alignItems="center"
          gap={1.5}
          px={3}
          py={1.5}
          borderRadius="full"
          bg="yellow.100"
          color="yellow.800"
          fontSize="11px"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="0.06em"
          mb={3.5}
        >
          <Icon as={FiClock} boxSize="11px" aria-hidden />
          In development
        </Badge>

        <Text
          as="h2"
          fontSize="22px"
          fontWeight="bold"
          color="gray.800"
          letterSpacing="-0.3px"
          mb="8px"
        >
          Analytics is on its way
        </Text>
        <Text
          fontSize="14px"
          color="gray.400"
          maxW="480px"
          lineHeight="1.6"
          mb="28px"
          textAlign="center"
        >
          We&apos;re building a reporting dashboard with prescription trends, prescriber breakdowns,
          and medication insights. In the meantime, you can download the underlying data from the{' '}
          <Text as="strong" fontWeight="semibold" color="gray.700">
            Exports
          </Text>{' '}
          tab.
        </Text>

        <Grid
          templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }}
          gap="14px"
          w="100%"
          maxW="760px"
          mb="28px"
        >
          {plannedFeatures.map((feature) => (
            <VStack
              key={feature.title}
              align="flex-start"
              spacing="10px"
              bg="#fafbfc"
              border="1px solid"
              borderColor="gray.100"
              borderRadius="12px"
              px="18px"
              pt="18px"
              pb="16px"
              textAlign="left"
              transition="all 0.15s"
              _hover={{
                borderColor: 'blue.100',
                bg: 'white',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
              }}
            >
              <IconBox boxSize={34} iconSize={16} bg="blue.50" color="blue.600">
                <Icon as={feature.icon} boxSize="16px" aria-hidden />
              </IconBox>
              <Text fontSize="13px" fontWeight="semibold" color="gray.800">
                {feature.title}
              </Text>
              <Text fontSize="12px" color="gray.400" lineHeight="1.5">
                {feature.description}
              </Text>
            </VStack>
          ))}
        </Grid>

        <Button
          h="38px"
          px="18px"
          fontSize="13px"
          fontWeight="semibold"
          colorScheme="blue"
          bg="blue.600"
          _hover={{ bg: 'blue.700' }}
          onClick={onGoToExports}
        >
          <Flex as="span" align="center" gap="7px">
            <DownloadIcon />
            Go to Exports
          </Flex>
        </Button>

        <Text fontSize="12px" color="gray.400" mt="18px">
          Have feedback on what you&apos;d like to see?{' '}
          <Link color="blue.600" fontWeight="medium" href="mailto:support@rxpad.com.au" isExternal>
            Tell us what matters most
          </Link>
        </Text>
      </VStack>
    </Box>
  )
}
